import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Music, Wand2, Trash2, Plus, Users, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIBeatsPage() {
  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  return (
    <div className="min-h-screen bg-dark pt-24 pb-16 px-4" data-testid="ai-beats-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <Badge className="bg-neon-magenta/20 text-neon-magenta border-0 mb-4">AI-Powered</Badge>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-3">
            Beat <span className="text-neon-cyan">Lab</span>
          </h1>
          <p className="text-zinc-400 max-w-lg mx-auto text-sm sm:text-base">
            Generate beat concepts with AI and collaborate with other producers in real-time.
          </p>
        </div>

        <Tabs defaultValue="generate" className="w-full">
          <TabsList className="w-full bg-zinc-900 border border-white/10 mb-8 h-12">
            <TabsTrigger value="generate" className="flex-1 data-[state=active]:bg-neon-cyan data-[state=active]:text-black" data-testid="tab-generate">
              <Wand2 className="w-4 h-4 mr-2" /> Generate
            </TabsTrigger>
            <TabsTrigger value="concepts" className="flex-1 data-[state=active]:bg-neon-cyan data-[state=active]:text-black" data-testid="tab-concepts">
              <Music className="w-4 h-4 mr-2" /> My Concepts
            </TabsTrigger>
            <TabsTrigger value="collabs" className="flex-1 data-[state=active]:bg-neon-cyan data-[state=active]:text-black" data-testid="tab-collabs">
              <Users className="w-4 h-4 mr-2" /> Collabs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generate">
            <GenerateTab token={token} headers={headers} />
          </TabsContent>
          <TabsContent value="concepts">
            <ConceptsTab token={token} headers={headers} />
          </TabsContent>
          <TabsContent value="collabs">
            <CollabsTab token={token} headers={headers} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function GenerateTab({ token, headers }) {
  const [form, setForm] = useState({ mood: "dark", genre: "trap", bpm: 140, key: "Am", description: "" });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!token) { toast.error("Sign in to generate beats"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/ai-beats/generate-description`, form, { headers });
      setResult(res.data);
      toast.success("Beat concept generated!");
    } catch { toast.error("Failed to generate"); }
    finally { setGenerating(false); }
  };

  const moods = ["dark", "chill", "hype", "melancholic", "aggressive", "dreamy", "ethereal", "bouncy"];
  const genres = ["trap", "hip-hop", "r&b", "lo-fi", "drill", "phonk", "pop", "afrobeat", "reggaeton"];
  const keys = ["Am", "Cm", "Em", "Gm", "Dm", "Fm", "Bbm", "C", "G", "D", "F", "Bb"];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Form */}
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="glass border border-white/10 rounded-xl p-6">
        <h3 className="font-heading text-lg uppercase mb-6 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-neon-cyan" /> Describe Your Beat
        </h3>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Mood</label>
            <div className="flex flex-wrap gap-2">
              {moods.map(m => (
                <button
                  key={m}
                  onClick={() => setForm(f => ({ ...f, mood: m }))}
                  className={`px-3 py-1.5 rounded-full text-xs capitalize transition ${
                    form.mood === m ? "bg-neon-cyan text-black font-bold" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
                  }`}
                  data-testid={`mood-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Genre</label>
            <Select value={form.genre} onValueChange={v => setForm(f => ({ ...f, genre: v }))}>
              <SelectTrigger className="bg-zinc-800 border-white/10" data-testid="genre-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {genres.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">BPM</label>
              <Input
                type="number" value={form.bpm}
                onChange={e => setForm(f => ({ ...f, bpm: parseInt(e.target.value) || 0 }))}
                className="bg-zinc-800 border-white/10"
                data-testid="bpm-input"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Key</label>
              <Select value={form.key} onValueChange={v => setForm(f => ({ ...f, key: v }))}>
                <SelectTrigger className="bg-zinc-800 border-white/10" data-testid="key-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {keys.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs text-zinc-400 uppercase tracking-wider mb-1 block">Additional Notes</label>
            <Textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the vibe... e.g., 'hard-hitting 808s with a haunting piano melody'"
              className="bg-zinc-800 border-white/10 min-h-[80px]"
              data-testid="description-input"
            />
          </div>

          <Button
            onClick={generate}
            disabled={generating}
            className="w-full bg-neon-cyan text-black font-bold uppercase tracking-wider"
            data-testid="generate-beat-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            {generating ? "Generating..." : "Generate Concept"}
          </Button>
        </div>
      </motion.div>

      {/* Result */}
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="glass border border-white/10 rounded-xl p-6">
        <h3 className="font-heading text-lg uppercase mb-6">Generated Concept</h3>
        {result ? (
          <div className="space-y-4">
            <div className="bg-zinc-800/60 rounded-lg p-4">
              <p className="text-zinc-300 text-sm leading-relaxed">{result.ai_description}</p>
            </div>
            <h4 className="text-xs text-zinc-400 uppercase tracking-wider">Suggested Stems</h4>
            <div className="space-y-2">
              {result.suggested_stems?.map((stem, i) => (
                <div key={i} className="flex items-center gap-3 bg-zinc-800/40 rounded-lg p-3">
                  <div className="w-8 h-8 rounded bg-neon-cyan/20 flex items-center justify-center">
                    <Music className="w-4 h-4 text-neon-cyan" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{stem.name}</p>
                    <p className="text-zinc-500 text-xs">{stem.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-zinc-600">
            <Wand2 className="w-12 h-12 mb-3" />
            <p className="text-sm">Generate a concept to see results</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function ConceptsTab({ token, headers }) {
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/api/ai-beats/concepts`, { headers })
      .then(res => setConcepts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const deleteConcept = async (id) => {
    try {
      await axios.delete(`${API}/api/ai-beats/concepts/${id}`, { headers });
      setConcepts(prev => prev.filter(c => c.concept_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  if (!token) return <p className="text-zinc-500 text-center py-12">Sign in to view your concepts</p>;
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-neon-cyan" /></div>;

  return (
    <div className="space-y-4">
      {concepts.length === 0 ? (
        <p className="text-zinc-500 text-center py-12">No concepts yet. Generate your first one!</p>
      ) : concepts.map(c => (
        <motion.div key={c.concept_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="glass border border-white/10 rounded-xl p-4 flex items-start justify-between"
        >
          <div>
            <div className="flex gap-2 mb-2">
              <Badge className="bg-neon-cyan/20 text-neon-cyan border-0 text-xs">{c.genre}</Badge>
              <Badge className="bg-white/10 text-zinc-300 border-0 text-xs">{c.bpm} BPM</Badge>
              <Badge className="bg-white/10 text-zinc-300 border-0 text-xs">{c.key}</Badge>
            </div>
            <p className="text-zinc-300 text-sm">{c.ai_description}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={() => deleteConcept(c.concept_id)} className="text-zinc-500 hover:text-red-400">
            <Trash2 className="w-4 h-4" />
          </Button>
        </motion.div>
      ))}
    </div>
  );
}

function CollabsTab({ token, headers }) {
  const [collabs, setCollabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", genre: "", bpm: 0, key: "" });
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [chatText, setChatText] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/collabs/?status=all`)
      .then(res => setCollabs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const createCollab = async () => {
    if (!token) { toast.error("Sign in first"); return; }
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    try {
      const res = await axios.post(`${API}/api/collabs/create`, form, { headers });
      setCollabs(prev => [res.data, ...prev]);
      setCreating(false);
      setForm({ title: "", description: "", genre: "", bpm: 0, key: "" });
      toast.success("Collaboration created!");
    } catch { toast.error("Failed to create"); }
  };

  const joinCollab = async (id) => {
    if (!token) { toast.error("Sign in first"); return; }
    try {
      await axios.post(`${API}/api/collabs/${id}/join`, {}, { headers });
      toast.success("Joined!");
      const res = await axios.get(`${API}/api/collabs/${id}`);
      setSelectedCollab(res.data);
    } catch { toast.error("Failed to join"); }
  };

  const sendMessage = async () => {
    if (!chatText.trim() || !selectedCollab) return;
    try {
      const res = await axios.post(`${API}/api/collabs/${selectedCollab.collab_id}/message`,
        { text: chatText }, { headers });
      setSelectedCollab(prev => ({ ...prev, messages: [...(prev.messages || []), res.data] }));
      setChatText("");
    } catch { toast.error("Failed to send"); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-neon-cyan" /></div>;

  if (selectedCollab) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => setSelectedCollab(null)} className="text-zinc-400" data-testid="collab-back">
          <ArrowRight className="w-4 h-4 mr-2 rotate-180" /> Back to Collabs
        </Button>
        <div className="glass border border-white/10 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-2">{selectedCollab.title}</h3>
          <p className="text-zinc-400 text-sm mb-4">{selectedCollab.description}</p>
          <div className="flex gap-2 mb-6">
            <Badge className="bg-neon-cyan/20 text-neon-cyan border-0">{selectedCollab.members?.length || 0} members</Badge>
            <Badge className="bg-white/10 text-zinc-300 border-0">{selectedCollab.status}</Badge>
          </div>

          {/* Chat */}
          <div className="bg-zinc-900 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto space-y-2">
            {(!selectedCollab.messages || selectedCollab.messages.length === 0) ? (
              <p className="text-zinc-600 text-sm text-center py-4">No messages yet. Start the conversation!</p>
            ) : selectedCollab.messages.map(m => (
              <div key={m.msg_id} className="text-sm">
                <span className="text-neon-cyan font-medium">{m.user_name}: </span>
                <span className="text-zinc-300">{m.text}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              value={chatText}
              onChange={e => setChatText(e.target.value)}
              placeholder="Type a message..."
              className="bg-zinc-800 border-white/10"
              onKeyDown={e => e.key === "Enter" && sendMessage()}
              data-testid="collab-chat-input"
            />
            <Button onClick={sendMessage} className="bg-neon-cyan text-black" data-testid="collab-send-btn">
              <MessageCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-white">Open Collaborations</h3>
        <Button onClick={() => setCreating(!creating)} className="bg-neon-cyan text-black" data-testid="create-collab-btn">
          <Plus className="w-4 h-4 mr-2" /> New Collab
        </Button>
      </div>

      <AnimatePresence>
        {creating && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="glass border border-neon-cyan/30 rounded-xl p-6 space-y-3"
          >
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Collab title..." className="bg-zinc-800 border-white/10" data-testid="collab-title-input" />
            <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe what you're looking for..." className="bg-zinc-800 border-white/10 min-h-[60px]" />
            <div className="flex gap-3">
              <Input value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))}
                placeholder="Genre" className="bg-zinc-800 border-white/10" />
              <Input type="number" value={form.bpm || ""} onChange={e => setForm(f => ({ ...f, bpm: parseInt(e.target.value) || 0 }))}
                placeholder="BPM" className="bg-zinc-800 border-white/10 w-24" />
              <Input value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value }))}
                placeholder="Key" className="bg-zinc-800 border-white/10 w-20" />
            </div>
            <Button onClick={createCollab} className="w-full bg-neon-cyan text-black font-bold" data-testid="submit-collab-btn">
              Create Collaboration
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {collabs.length === 0 ? (
        <p className="text-zinc-500 text-center py-12">No collaborations yet. Create the first one!</p>
      ) : collabs.map(c => (
        <motion.div key={c.collab_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="glass border border-white/10 rounded-xl p-4 hover:border-neon-cyan/30 transition cursor-pointer"
          onClick={() => { setSelectedCollab(c); }}
          data-testid={`collab-card-${c.collab_id}`}
        >
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-white font-bold">{c.title}</h4>
              <p className="text-zinc-400 text-sm mt-1">{c.description || "No description"}</p>
              <div className="flex gap-2 mt-2">
                {c.genre && <Badge className="bg-neon-magenta/20 text-neon-magenta border-0 text-xs">{c.genre}</Badge>}
                <Badge className="bg-white/10 text-zinc-300 border-0 text-xs">{c.members?.length || 0} members</Badge>
                <Badge className={`border-0 text-xs ${c.status === "open" ? "bg-green-500/20 text-green-400" : "bg-zinc-700 text-zinc-400"}`}>
                  {c.status}
                </Badge>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); joinCollab(c.collab_id); }}
              className="border-neon-cyan text-neon-cyan hover:bg-neon-cyan hover:text-black text-xs"
              data-testid={`join-collab-${c.collab_id}`}
            >
              Join
            </Button>
          </div>
        </motion.div>
      ))}
    </div>
  );
}
